import React from 'react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { newActionNode, newDecisionNode } from '../utils/workflowTree';
import WorkflowStepEditor from './WorkflowStepEditor';

/** Inserteur discret entre deux étapes (révélé au survol du gap). */
function AddSlot({ onAddAction, onAddDecision }) {
  return (
    <div className="wf-add">
      <button className="wf-add-btn" onClick={onAddAction}>+ Action</button>
      <button className="wf-add-btn dec" onClick={onAddDecision}>+ Décision</button>
    </div>
  );
}

/** Une rangée triable : badge sur le rail + carte (l'éditeur reçoit la poignée de drag). */
function SortableRow({ node, onChange, onRemove, depth, isFirst, isLast }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isDecision = node.type === 'decision';
  return (
    <div ref={setNodeRef} style={style} className={`wf-item ${isFirst ? 'is-first' : ''} ${isLast ? 'is-last' : ''}`}>
      <div className="wf-rail-col">
        <span className={`wf-badge ${isDecision ? 'decision' : 'action'}`} aria-hidden>{isDecision ? '⑂' : '▸'}</span>
      </div>
      <WorkflowStepEditor node={node} onChange={onChange} onRemove={onRemove} depth={depth} dragHandle={{ ...attributes, ...listeners }} />
    </div>
  );
}

/**
 * Liste ordonnée des frères d'UNE branche. Rail + badges + cartes. Chaque liste a
 * son propre DndContext borné à ses ids (branches isolées).
 */
export default function WorkflowStepList({ nodes, onChange, depth = 0 }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const insertAt = (index, node) => { const c = [...nodes]; c.splice(index, 0, node); onChange(c); };
  const updateAt = (index, patch) => onChange(nodes.map((n, i) => (i === index ? { ...n, ...patch } : n)));
  const removeAt = (index) => onChange(nodes.filter((_, i) => i !== index));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = nodes.findIndex((n) => n.id === active.id);
    const to = nodes.findIndex((n) => n.id === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(nodes, from, to));
  };

  return (
    <div className="wf-seq">
      <AddSlot onAddAction={() => insertAt(0, newActionNode())} onAddDecision={() => insertAt(0, newDecisionNode())} />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={nodes.map((n) => n.id)} strategy={verticalListSortingStrategy}>
          {nodes.map((node, i) => (
            <React.Fragment key={node.id}>
              <SortableRow node={node} onChange={(patch) => updateAt(i, patch)} onRemove={() => removeAt(i)}
                depth={depth} isFirst={i === 0} isLast={i === nodes.length - 1} />
              <AddSlot onAddAction={() => insertAt(i + 1, newActionNode())} onAddDecision={() => insertAt(i + 1, newDecisionNode())} />
            </React.Fragment>
          ))}
        </SortableContext>
      </DndContext>
      {nodes.length === 0 && <div className="wf-empty-branch">Aucune étape pour l’instant.</div>}
    </div>
  );
}
